import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { formatUploadError, downloadUploadErrorsCsv } from '../utils/uploadErrors';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Plus, Trash2, Edit2, Upload, Download, Calculator, CheckCircle,
  Target, Zap, TrendingUp, IndianRupee, Users, Package,
  FileSpreadsheet, ChevronRight, Award, Layers, AlertTriangle, MinusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const PPUSalary = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(false);

  // Config state
  const [taskTypes, setTaskTypes] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [targets, setTargets] = useState([]);
  const [multipliers, setMultipliers] = useState([]);
  const [deductionRules, setDeductionRules] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  // Entries state
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entryMonth, setEntryMonth] = useState(new Date().getMonth() + 1);
  const [entryYear, setEntryYear] = useState(new Date().getFullYear());

  // Calculation state
  const [calculations, setCalculations] = useState([]);
  const [calcMonth, setCalcMonth] = useState(new Date().getMonth() + 1);
  const [calcYear, setCalcYear] = useState(new Date().getFullYear());
  const [selectedCalc, setSelectedCalc] = useState(null);

  // Report state
  const [report, setReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editItem, setEditItem] = useState(null);

  // Bulk upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState('daily');
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Form state
  const [form, setForm] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [ttRes, rcRes, tgRes, mlRes, dsRes, dpRes, empRes, prjRes] = await Promise.all([
        api.get('/ppu/task-types'),
        api.get('/ppu/rate-cards'),
        api.get('/ppu/targets'),
        api.get('/ppu/multipliers'),
        api.get('/designations'),
        api.get('/departments'),
        api.get('/employees'),
        api.get('/ppu/projects')
      ]);
      setTaskTypes(ttRes.data);
      setRateCards(rcRes.data);
      setTargets(tgRes.data);
      setMultipliers(mlRes.data);
      setDesignations(Array.isArray(dsRes.data) ? dsRes.data : dsRes.data?.designations || []);
      setDepartments(Array.isArray(dpRes.data) ? dpRes.data : dpRes.data?.departments || []);
      setEmployees(Array.isArray(empRes.data) ? empRes.data : empRes.data?.employees || []);
      setProjects(prjRes.data);
    } catch (err) {
      console.error('Fetch error:', err);
    }
    // Fetch deduction rules separately so it doesn't crash everything
    try {
      const drRes = await api.get('/ppu/deduction-rules');
      setDeductionRules(drRes.data);
    } catch (err) {
      setDeductionRules([]);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getTaskName = (id) => taskTypes.find(t => t.id === id)?.name || 'Unknown';
  const getDesigName = (id) => designations.find(d => d.id === id)?.title || 'All';
  const getProjectName = (id) => id ? (projects.find(p => p.id === id)?.name || 'Unknown') : 'General';
  const getEmpName = (id) => {
    const e = employees.find(emp => emp.id === id);
    return e ? (e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim()) : 'Unknown';
  };
  const getEmpCode = (id) => employees.find(e => e.id === id)?.employee_id || '';

  // ===== DIALOG HANDLERS =====
  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    if (type === 'task_type') {
      setForm(item ? { name: item.name, description: item.description } : { name: '', description: '' });
    } else if (type === 'project') {
      setForm(item ? { name: item.name, client: item.client || '', region: item.region || '', description: item.description || '' } : { name: '', client: '', region: '', description: '' });
    } else if (type === 'rate_card') {
      setForm(item ? {
        task_type_id: item.task_type_id, designation_id: item.designation_id,
        project_id: item.project_id || '', base_rate: item.base_rate, slabs: item.slabs || []
      } : { task_type_id: '', designation_id: '', project_id: '', base_rate: '', slabs: [] });
    } else if (type === 'target') {
      setForm(item ? {
        name: item.name, description: item.description, target_type: item.target_type,
        task_type_id: item.task_type_id || '', project_id: item.project_id || '',
        target_units: item.target_units, bonus_amount: item.bonus_amount, designation_id: item.designation_id || '',
        frequency: item.frequency || 'monthly'
      } : { name: '', description: '', target_type: 'total_units', task_type_id: '', project_id: '', target_units: '', bonus_amount: '', designation_id: '', frequency: 'monthly' });
    } else if (type === 'deduction_rule') {
      setForm(item ? {
        name: item.name, description: item.description, task_type_id: item.task_type_id || '',
        deduction_type: item.deduction_type, flat_rate: item.flat_rate || 0,
        slabs: item.slabs || [], designation_id: item.designation_id || ''
      } : { name: '', description: '', task_type_id: '', deduction_type: 'flat', flat_rate: '', slabs: [{ min_failed: 1, max_failed: 5, deduction_amount: '' }], designation_id: '' });
    } else if (type === 'multiplier') {
      setForm(item ? {
        name: item.name, description: item.description,
        required_task_type_ids: item.required_task_type_ids || [],
        multiplier: item.multiplier, apply_to: item.apply_to
      } : { name: '', description: '', required_task_type_ids: [], multiplier: '', apply_to: 'daily' });
    } else if (type === 'entry') {
      setForm({ employee_id: '', date: new Date().toISOString().slice(0, 10), task_type_id: '', project_id: '', units: '', notes: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (dialogType === 'task_type') {
        if (editItem) {
          await api.put(`/ppu/task-types/${editItem.id}`, form);
        } else {
          await api.post('/ppu/task-types', form);
        }
        toast.success('Task type saved');
      } else if (dialogType === 'project') {
        if (editItem) {
          await api.put(`/ppu/projects/${editItem.id}`, form);
        } else {
          await api.post('/ppu/projects', form);
        }
        toast.success('Project saved');
      } else if (dialogType === 'rate_card') {
        const payload = { ...form, base_rate: parseFloat(form.base_rate), slabs: form.slabs || [] };
        if (editItem) {
          await api.put(`/ppu/rate-cards/${editItem.id}`, payload);
        } else {
          await api.post('/ppu/rate-cards', payload);
        }
        toast.success('Rate card saved');
      } else if (dialogType === 'target') {
        const payload = { ...form, target_units: parseInt(form.target_units), bonus_amount: parseFloat(form.bonus_amount) };
        if (editItem) {
          await api.put(`/ppu/targets/${editItem.id}`, payload);
        } else {
          await api.post('/ppu/targets', payload);
        }
        toast.success('Target saved');
      } else if (dialogType === 'multiplier') {
        const payload = { ...form, multiplier: parseFloat(form.multiplier) };
        if (editItem) {
          await api.put(`/ppu/multipliers/${editItem.id}`, payload);
        } else {
          await api.post('/ppu/multipliers', payload);
        }
        toast.success('Multiplier saved');
      } else if (dialogType === 'entry') {
        await api.post('/ppu/entries', { ...form, units: parseInt(form.units), failed_units: parseInt(form.failed_units || 0) });
        toast.success('Entry saved');
        fetchEntries();
      } else if (dialogType === 'deduction_rule') {
        const payload = {
          ...form,
          flat_rate: parseFloat(form.flat_rate || 0),
          slabs: (form.slabs || []).map(s => ({ min_failed: parseInt(s.min_failed), max_failed: s.max_failed ? parseInt(s.max_failed) : null, deduction_amount: parseFloat(s.deduction_amount) }))
        };
        if (editItem) {
          await api.put(`/ppu/deduction-rules/${editItem.id}`, payload);
        } else {
          await api.post('/ppu/deduction-rules', payload);
        }
        toast.success('Deduction rule saved');
      }
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/ppu/${type}/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // ===== ENTRIES =====
  const fetchEntries = useCallback(async () => {
    try {
      const res = await api.get(`/ppu/entries?month=${entryMonth}&year=${entryYear}`);
      setEntries(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [entryMonth, entryYear]);

  useEffect(() => { if (activeTab === 'entries') fetchEntries(); }, [activeTab, fetchEntries]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/ppu/entries/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ppu_entry_template.xlsx';
      link.click();
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // ===== BULK UPLOAD HANDLERS =====
  const downloadMonthlyTemplate = async () => {
    try {
      const res = await api.get('/ppu/entries/template-monthly', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ppu_monthly_template.xlsx';
      link.click();
      toast.success('Monthly template downloaded');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      setUploading(true);
      let res;
      if (uploadType === 'daily') {
        res = await api.post('/ppu/entries/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post(`/ppu/entries/upload-monthly?month=${uploadMonth}&year=${uploadYear}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setUploadResult(res.data);
      toast.success(`Created: ${res.data.created}, Updated: ${res.data.updated}`);
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
      }
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadAndCalculate = async () => {
    if (!uploadFile) { toast.error('Please select a file'); return; }
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      setUploading(true);
      const res = await api.post(
        `/ppu/upload-and-calculate?month=${uploadMonth}&year=${uploadYear}&upload_type=${uploadType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadResult(res.data.upload);
      toast.success(`Uploaded & calculated for ${res.data.total_employees} employees`);
      if (res.data.upload?.errors?.length > 0) {
        toast.error(`${res.data.upload.errors.length} rows had errors`);
      }
      fetchEntries();
      fetchCalculations();
      setShowUploadDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload & Calculate failed');
    } finally {
      setUploading(false);
    }
  };

  // ===== CALCULATIONS =====
  const runCalculation = async () => {
    try {
      setLoading(true);
      const res = await api.post('/ppu/calculate', { month: calcMonth, year: calcYear });
      toast.success(`Calculated for ${res.data.total_employees} employees`);
      fetchCalculations();
    } catch (err) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalculations = useCallback(async () => {
    try {
      const res = await api.get(`/ppu/calculations?month=${calcMonth}&year=${calcYear}`);
      setCalculations(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [calcMonth, calcYear]);

  useEffect(() => { if (activeTab === 'calculator') fetchCalculations(); }, [activeTab, fetchCalculations]);

  const approveCalc = async (id) => {
    try {
      await api.put(`/ppu/calculations/${id}/approve`);
      toast.success('Approved');
      fetchCalculations();
    } catch (err) {
      toast.error('Approval failed');
    }
  };

  const approveAll = async () => {
    try {
      const res = await api.put(`/ppu/calculations/approve-all?month=${calcMonth}&year=${calcYear}`);
      toast.success(res.data.message);
      fetchCalculations();
    } catch (err) {
      toast.error('Approval failed');
    }
  };

  const exportExcel = async (m, y) => {
    try {
      const res = await api.get(`/ppu/export/excel?month=${m}&year=${y}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PPU_Report_${MONTH_NAMES[m - 1]}_${y}.xlsx`;
      link.click();
      toast.success('Excel downloaded');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const downloadPayslip = async (calcId, empCode, m, y) => {
    try {
      const res = await api.get(`/ppu/payslip/${calcId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PPU_Payslip_${empCode}_${MONTH_NAMES[m - 1]}_${y}.pdf`;
      link.click();
      toast.success('Payslip downloaded');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // ===== REPORTS =====
  const fetchReport = useCallback(async () => {
    try {
      const res = await api.get(`/ppu/reports/summary?month=${reportMonth}&year=${reportYear}`);
      setReport(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [reportMonth, reportYear]);

  useEffect(() => { if (activeTab === 'reports') fetchReport(); }, [activeTab, fetchReport]);

  // ===== SLAB HELPERS =====
  const addSlab = () => {
    setForm(prev => ({
      ...prev,
      slabs: [...(prev.slabs || []), { min_units: 0, max_units: null, rate: 0 }]
    }));
  };

  const updateSlab = (idx, field, value) => {
    setForm(prev => {
      const slabs = [...prev.slabs];
      slabs[idx] = { ...slabs[idx], [field]: value === '' ? null : parseFloat(value) };
      return { ...prev, slabs };
    });
  };

  const removeSlab = (idx) => {
    setForm(prev => ({
      ...prev,
      slabs: prev.slabs.filter((_, i) => i !== idx)
    }));
  };

  const toggleTaskType = (id) => {
    setForm(prev => {
      const ids = prev.required_task_type_ids || [];
      return {
        ...prev,
        required_task_type_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
      };
    });
  };

  // Group entries by employee for display
  const groupedEntries = entries.reduce((acc, e) => {
    const key = e.employee_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="ppu-salary-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">PPU Salary Calculator</h1>
            <p className="text-slate-500 text-sm">Pay Per Unit payout model with slabs, targets &amp; multipliers</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="config" className="gap-1.5" data-testid="config-tab">
              <Layers size={14} /> Configuration
            </TabsTrigger>
            <TabsTrigger value="entries" className="gap-1.5" data-testid="entries-tab">
              <FileSpreadsheet size={14} /> Daily Entries
            </TabsTrigger>
            <TabsTrigger value="calculator" className="gap-1.5" data-testid="calculator-tab">
              <Calculator size={14} /> Salary Calculator
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-1.5" data-testid="reports-tab">
              <TrendingUp size={14} /> Reports
            </TabsTrigger>
          </TabsList>

          {/* ===== CONFIGURATION TAB ===== */}
          <TabsContent value="config" className="space-y-6 mt-6">
            {/* Task Types */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><Package size={18} className="text-indigo-500" /> Task Types</CardTitle>
                  <CardDescription>Define the unit types for PPU calculation</CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('task_type')} data-testid="add-task-type-btn">
                  <Plus size={14} className="mr-1" /> Add Task Type
                </Button>
              </CardHeader>
              <CardContent>
                {taskTypes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No task types configured. Add your first task type to get started.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {taskTypes.map(t => (
                      <div key={t.id} className="border rounded-lg p-4 flex items-start justify-between bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`task-type-${t.id}`}>
                        <div>
                          <p className="font-semibold text-slate-800">{t.name}</p>
                          <p className="text-xs text-slate-500 mt-1">{t.description || 'No description'}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('task_type', t)}><Edit2 size={13} /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('task-types', t.id)}><Trash2 size={13} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Projects */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><ChevronRight size={18} className="text-cyan-500" /> Projects</CardTitle>
                  <CardDescription>Define client/region projects for rate bifurcation</CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('project')} data-testid="add-project-btn">
                  <Plus size={14} className="mr-1" /> Add Project
                </Button>
              </CardHeader>
              <CardContent>
                {projects.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No projects configured. Add projects to enable project-wise rate cards.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {projects.map(p => (
                      <div key={p.id} className="border rounded-lg p-4 flex items-start justify-between bg-cyan-50/50 hover:bg-cyan-100/50 transition-colors" data-testid={`project-${p.id}`}>
                        <div>
                          <p className="font-semibold text-slate-800">{p.name}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {p.client && <Badge variant="outline" className="text-xs">{p.client}</Badge>}
                            {p.region && <Badge variant="outline" className="text-xs bg-blue-50">{p.region}</Badge>}
                          </div>
                          {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('project', p)}><Edit2 size={13} /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('projects', p.id)}><Trash2 size={13} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rate Cards */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><IndianRupee size={18} className="text-emerald-500" /> Rate Cards</CardTitle>
                  <CardDescription>Set per-unit rates by project, task type and designation (with optional slabs)</CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('rate_card')} disabled={taskTypes.length === 0} data-testid="add-rate-card-btn">
                  <Plus size={14} className="mr-1" /> Add Rate Card
                </Button>
              </CardHeader>
              <CardContent>
                {rateCards.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No rate cards configured yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 font-medium">Project</th>
                          <th className="pb-2 font-medium">Task Type</th>
                          <th className="pb-2 font-medium">Designation</th>
                          <th className="pb-2 font-medium">Base Rate</th>
                          <th className="pb-2 font-medium">Slabs</th>
                          <th className="pb-2 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rateCards.map(rc => (
                          <tr key={rc.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`rate-card-${rc.id}`}>
                            <td className="py-3">
                              <Badge variant={rc.project_id ? "default" : "secondary"} className={rc.project_id ? "bg-cyan-100 text-cyan-800" : ""}>{getProjectName(rc.project_id)}</Badge>
                            </td>
                            <td className="py-3 font-medium">{getTaskName(rc.task_type_id)}</td>
                            <td className="py-3">{getDesigName(rc.designation_id)}</td>
                            <td className="py-3 text-emerald-600 font-semibold">{rc.base_rate}/unit</td>
                            <td className="py-3">
                              {rc.slabs?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {rc.slabs.map((s, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {s.min_units}+ : {s.rate}/unit
                                    </Badge>
                                  ))}
                                </div>
                              ) : <span className="text-slate-400">Flat rate</span>}
                            </td>
                            <td className="py-3 text-right">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('rate_card', rc)}><Edit2 size={13} /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('rate-cards', rc.id)}><Trash2 size={13} /></Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Targets */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><Target size={18} className="text-amber-500" /> Target Bonuses</CardTitle>
                  <CardDescription>Lump sum bonuses when employees hit unit targets</CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('target')} data-testid="add-target-btn">
                  <Plus size={14} className="mr-1" /> Add Target
                </Button>
              </CardHeader>
              <CardContent>
                {targets.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No targets configured.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {targets.map(t => (
                      <div key={t.id} className="border rounded-lg p-4 bg-amber-50/50" data-testid={`target-${t.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">{t.name}</p>
                            <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="outline">{t.target_type === 'individual_task' ? getTaskName(t.task_type_id) : 'All Tasks'}</Badge>
                              {t.project_id && <Badge className="bg-cyan-100 text-cyan-700 text-xs">{getProjectName(t.project_id)}</Badge>}
                              {!t.project_id && <Badge variant="secondary" className="text-xs">All Projects</Badge>}
                              <Badge variant="outline">{t.target_units} units</Badge>
                              <Badge className="bg-emerald-100 text-emerald-700">{t.bonus_amount}</Badge>
                              <Badge className={`text-xs ${t.frequency === 'daily' ? 'bg-blue-100 text-blue-700' : t.frequency === 'weekly' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>{(t.frequency || 'monthly').charAt(0).toUpperCase() + (t.frequency || 'monthly').slice(1)}</Badge>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('target', t)}><Edit2 size={13} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('targets', t.id)}><Trash2 size={13} /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Multipliers */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><Zap size={18} className="text-purple-500" /> Combination Multipliers</CardTitle>
                  <CardDescription>Bonus multiplier when employee completes multiple task types</CardDescription>
                </div>
                <Button size="sm" onClick={() => openDialog('multiplier')} disabled={taskTypes.length < 2} data-testid="add-multiplier-btn">
                  <Plus size={14} className="mr-1" /> Add Multiplier
                </Button>
              </CardHeader>
              <CardContent>
                {multipliers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No multipliers configured.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {multipliers.map(m => (
                      <div key={m.id} className="border rounded-lg p-4 bg-purple-50/50" data-testid={`multiplier-${m.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">{m.name}</p>
                            <p className="text-xs text-slate-500 mt-1">{m.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(m.required_task_type_ids || []).map(id => (
                                <Badge key={id} variant="outline" className="text-xs">{getTaskName(id)}</Badge>
                              ))}
                              <Badge className="bg-purple-100 text-purple-700">{m.multiplier}x</Badge>
                              <Badge variant="secondary">{m.apply_to}</Badge>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('multiplier', m)}><Edit2 size={13} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('multipliers', m.id)}><Trash2 size={13} /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deduction Rules */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2"><AlertTriangle size={18} className="text-red-500" /> Deduction Rules</CardTitle>
                  <CardDescription>Deductions for failed/incomplete tasks (flat rate or slab-based)</CardDescription>
                </div>
                <Button size="sm" variant="destructive" onClick={() => openDialog('deduction_rule')} data-testid="add-deduction-btn">
                  <Plus size={14} className="mr-1" /> Add Rule
                </Button>
              </CardHeader>
              <CardContent>
                {deductionRules.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No deduction rules configured.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {deductionRules.map(r => (
                      <div key={r.id} className="border border-red-200 rounded-lg p-4 bg-red-50/50" data-testid={`deduction-${r.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">{r.name}</p>
                            <p className="text-xs text-slate-500 mt-1">{r.description}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="outline">{r.task_type_id ? getTaskName(r.task_type_id) : 'All Tasks'}</Badge>
                              <Badge className={r.deduction_type === 'flat' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                                {r.deduction_type === 'flat' ? `Rs.${r.flat_rate}/failed unit` : `${(r.slabs || []).length} slabs`}
                              </Badge>
                              {r.designation_id && <Badge variant="secondary" className="text-xs">{getDesigName(r.designation_id)}</Badge>}
                            </div>
                            {r.deduction_type === 'slab' && r.slabs?.length > 0 && (
                              <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                                {r.slabs.map((s, i) => (
                                  <div key={i}>{s.min_failed}-{s.max_failed || '+'} failed: Rs.{s.deduction_amount}</div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog('deduction_rule', r)}><Edit2 size={13} /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete('deduction-rules', r.id)}><Trash2 size={13} /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="entries" className="space-y-6 mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={String(entryMonth)} onValueChange={v => setEntryMonth(parseInt(v))}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(entryYear)} onValueChange={v => setEntryYear(parseInt(v))}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchEntries}>Refresh</Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => { setUploadType('daily'); setUploadFile(null); setUploadResult(null); setShowUploadDialog(true); }} className="gap-1.5" data-testid="bulk-upload-btn">
                <Upload size={14} /> Bulk Upload
              </Button>
              <Button onClick={() => openDialog('entry')} disabled={taskTypes.length === 0} data-testid="add-entry-btn">
                <Plus size={14} className="mr-1" /> Add Entry
              </Button>
            </div>

            {Object.keys(groupedEntries).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <FileSpreadsheet size={40} className="mx-auto mb-3 text-slate-300" />
                  <p>No entries for {MONTH_NAMES[entryMonth - 1]} {entryYear}</p>
                  <p className="text-xs mt-1">Add entries manually or upload an Excel file</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedEntries).map(([empId, empEntries]) => (
                  <Card key={empId}>
                    <CardHeader className="py-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users size={16} className="text-indigo-500" />
                        {getEmpName(empId)} <Badge variant="outline" className="text-xs">{getEmpCode(empId)}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-slate-500">
                            <th className="pb-2 font-medium">Date</th>
                            <th className="pb-2 font-medium">Task Type</th>
                            <th className="pb-2 font-medium">Project</th>
                            <th className="pb-2 font-medium">Units</th>
                            <th className="pb-2 font-medium">Notes</th>
                            <th className="pb-2 font-medium text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {empEntries.sort((a, b) => a.date.localeCompare(b.date)).map(entry => (
                            <tr key={entry.id} className="border-b last:border-0">
                              <td className="py-2">{entry.date}</td>
                              <td className="py-2">{getTaskName(entry.task_type_id)}</td>
                              <td className="py-2"><Badge variant="outline" className="text-xs">{getProjectName(entry.project_id)}</Badge></td>
                              <td className="py-2 font-semibold text-indigo-600">{entry.units}</td>
                              <td className="py-2 text-slate-500 text-xs">{entry.notes}</td>
                              <td className="py-2 text-right">
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDelete('entries', entry.id)}>
                                  <Trash2 size={12} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== SALARY CALCULATOR TAB ===== */}
          <TabsContent value="calculator" className="space-y-6 mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={String(calcMonth)} onValueChange={v => setCalcMonth(parseInt(v))}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(calcYear)} onValueChange={v => setCalcYear(parseInt(v))}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={runCalculation} disabled={loading} className="gap-1.5" data-testid="run-calc-btn">
                <Calculator size={14} /> {loading ? 'Calculating...' : 'Calculate Salaries'}
              </Button>
              <Button variant="outline" onClick={() => { setUploadType('monthly'); setUploadMonth(calcMonth); setUploadYear(calcYear); setUploadFile(null); setUploadResult(null); setShowUploadDialog(true); }} className="gap-1.5" data-testid="upload-calc-btn">
                <Upload size={14} /> Upload &amp; Calculate
              </Button>
              <div className="flex-1" />
              {calculations.some(c => c.status === 'draft') && (
                <Button variant="outline" onClick={approveAll} className="gap-1.5 text-emerald-600" data-testid="approve-all-btn">
                  <CheckCircle size={14} /> Approve All
                </Button>
              )}
              {calculations.length > 0 && (
                <Button variant="outline" onClick={() => exportExcel(calcMonth, calcYear)} className="gap-1.5" data-testid="export-excel-btn">
                  <Download size={14} /> Export Excel
                </Button>
              )}
            </div>

            {/* Summary cards */}
            {calculations.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-indigo-600">Employees</p>
                    <p className="text-2xl font-bold text-indigo-700">{calculations.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-emerald-600">Total Payout</p>
                    <p className="text-2xl font-bold text-emerald-700">{calculations.reduce((s, c) => s + c.grand_total, 0).toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-amber-600">Total Units</p>
                    <p className="text-2xl font-bold text-amber-700">{calculations.reduce((s, c) => s + c.total_units, 0).toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <p className="text-xs text-purple-600">Avg Payout</p>
                    <p className="text-2xl font-bold text-purple-700">{Math.round(calculations.reduce((s, c) => s + c.grand_total, 0) / calculations.length).toLocaleString('en-IN')}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {calculations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <Calculator size={40} className="mx-auto mb-3 text-slate-300" />
                  <p>No calculations for {MONTH_NAMES[calcMonth - 1]} {calcYear}</p>
                  <p className="text-xs mt-1">Click "Calculate Salaries" to run the PPU engine</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-slate-500 bg-slate-50">
                        <th className="p-3 font-medium">Employee</th>
                        <th className="p-3 font-medium">Code</th>
                        <th className="p-3 font-medium text-center">Units</th>
                        <th className="p-3 font-medium text-right">Base</th>
                        <th className="p-3 font-medium text-right">Slab Bonus</th>
                        <th className="p-3 font-medium text-right">Target Bonus</th>
                        <th className="p-3 font-medium text-right">Multiplier</th>
                        <th className="p-3 font-medium text-right">Deductions</th>
                        <th className="p-3 font-medium text-right">Grand Total</th>
                        <th className="p-3 font-medium text-center">Status</th>
                        <th className="p-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculations.sort((a, b) => b.grand_total - a.grand_total).map(c => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedCalc(c)} data-testid={`calc-row-${c.employee_id}`}>
                          <td className="p-3 font-medium">{c.employee_name}</td>
                          <td className="p-3 text-slate-500">{c.employee_code}</td>
                          <td className="p-3 text-center font-semibold text-indigo-600">{c.total_units}</td>
                          <td className="p-3 text-right">{c.total_base.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-right text-amber-600">{c.total_slab_bonus > 0 ? `+${c.total_slab_bonus.toLocaleString('en-IN')}` : '-'}</td>
                          <td className="p-3 text-right text-emerald-600">{c.total_target_bonus > 0 ? `+${c.total_target_bonus.toLocaleString('en-IN')}` : '-'}</td>
                          <td className="p-3 text-right text-purple-600">{c.total_multiplier_bonus > 0 ? `+${c.total_multiplier_bonus.toLocaleString('en-IN')}` : '-'}</td>
                          <td className="p-3 text-right text-red-600">{(c.total_deductions || 0) > 0 ? `-${c.total_deductions.toLocaleString('en-IN')}` : '-'}</td>
                          <td className="p-3 text-right font-bold text-slate-900">{c.grand_total.toLocaleString('en-IN')}</td>
                          <td className="p-3 text-center">
                            <Badge className={c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : c.status === 'paid' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                              {c.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            {c.status === 'draft' && (
                              <Button size="sm" variant="outline" className="text-emerald-600 h-7 mr-1" onClick={(e) => { e.stopPropagation(); approveCalc(c.id); }}>
                                <CheckCircle size={12} className="mr-1" /> Approve
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7" onClick={(e) => { e.stopPropagation(); downloadPayslip(c.id, c.employee_code, c.month, c.year); }} data-testid={`payslip-btn-${c.employee_id}`}>
                              <Download size={12} className="mr-1" /> Payslip
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Detailed breakdown modal */}
            {selectedCalc && (
              <Dialog open={!!selectedCalc} onOpenChange={() => setSelectedCalc(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <IndianRupee size={18} className="text-emerald-500" />
                      PPU Breakdown: {selectedCalc.employee_name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-3 rounded-lg flex justify-between items-center">
                      <span className="text-sm text-slate-600">{MONTH_NAMES[selectedCalc.month - 1]} {selectedCalc.year}</span>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadPayslip(selectedCalc.id, selectedCalc.employee_code, selectedCalc.month, selectedCalc.year)} data-testid="dialog-payslip-btn">
                          <Download size={12} className="mr-1" /> Download Payslip
                        </Button>
                        <span className="font-bold text-xl text-emerald-700">{selectedCalc.grand_total.toLocaleString('en-IN')}</span>
                      </div>
                    </div>

                    {/* Project-wise breakdown */}
                    {(selectedCalc.project_summary || []).length > 0 ? (
                      selectedCalc.project_summary.map((proj, pi) => (
                        <div key={pi} className="border rounded-lg overflow-hidden">
                          <div className="bg-cyan-50 px-3 py-2 flex justify-between items-center border-b">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-cyan-100 text-cyan-800">{proj.project_name}</Badge>
                              {proj.client && <span className="text-xs text-slate-500">{proj.client}</span>}
                              {proj.region && <span className="text-xs text-slate-500">| {proj.region}</span>}
                            </div>
                            <span className="font-semibold text-cyan-800">{proj.total_amount.toLocaleString('en-IN')}</span>
                          </div>
                          <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 border-b"><th className="p-2 text-left">Task</th><th className="p-2 text-center">Units</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Base</th><th className="p-2 text-right">Slab</th><th className="p-2 text-right font-bold">Total</th></tr></thead>
                            <tbody>
                              {proj.tasks.map((b, i) => (
                                <tr key={i} className="border-b last:border-0">
                                  <td className="p-2">{b.task_type_name}</td>
                                  <td className="p-2 text-center font-semibold">{b.units}</td>
                                  <td className="p-2 text-right">{b.base_rate}/unit</td>
                                  <td className="p-2 text-right">{b.base_amount.toLocaleString('en-IN')}</td>
                                  <td className="p-2 text-right text-amber-600">{b.slab_amount > 0 ? `+${b.slab_amount.toLocaleString('en-IN')}` : '-'}</td>
                                  <td className="p-2 text-right font-bold">{b.total.toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))
                    ) : (
                      <div>
                        <p className="font-medium text-sm mb-2">Task Breakdown</p>
                        <table className="w-full text-sm border rounded">
                          <thead><tr className="bg-slate-50 border-b"><th className="p-2 text-left">Task</th><th className="p-2 text-center">Units</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Base</th><th className="p-2 text-right">Slab Bonus</th><th className="p-2 text-right font-bold">Total</th></tr></thead>
                          <tbody>
                            {selectedCalc.breakdown.map((b, i) => (
                              <tr key={i} className="border-b last:border-0">
                                <td className="p-2">{b.task_type_name}</td>
                                <td className="p-2 text-center font-semibold">{b.units}</td>
                                <td className="p-2 text-right">{b.base_rate}/unit</td>
                                <td className="p-2 text-right">{b.base_amount.toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right text-amber-600">{b.slab_amount > 0 ? `+${b.slab_amount.toLocaleString('en-IN')}` : '-'}</td>
                                <td className="p-2 text-right font-bold">{b.total.toLocaleString('en-IN')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Targets */}
                    {selectedCalc.target_bonuses?.length > 0 && (
                      <div>
                        <p className="font-medium text-sm mb-2 flex items-center gap-1"><Target size={14} className="text-amber-500" /> Target Bonuses</p>
                        {selectedCalc.target_bonuses.map((t, i) => (
                          <div key={i} className={`flex items-center justify-between p-2 rounded mb-1 ${t.achieved ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                            <div>
                              <p className="text-sm font-medium">{t.target_name}</p>
                              <div className="flex items-center gap-1.5">
                                <p className="text-xs text-slate-500">{t.description}</p>
                                {t.project_name && t.project_name !== 'All' && (
                                  <Badge className="bg-cyan-100 text-cyan-700 text-xs h-4">{t.project_name}</Badge>
                                )}
                                {t.frequency && (
                                  <Badge className={`text-xs h-4 ${t.frequency === 'daily' ? 'bg-blue-100 text-blue-700' : t.frequency === 'weekly' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>{t.frequency}</Badge>
                                )}
                                {t.times_achieved > 1 && (
                                  <Badge className="bg-green-100 text-green-700 text-xs h-4">x{t.times_achieved}</Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              {t.achieved ? (
                                <Badge className="bg-emerald-100 text-emerald-700">+{t.bonus.toLocaleString('en-IN')}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-400">Not achieved</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Multipliers */}
                    {selectedCalc.multiplier_bonuses?.length > 0 && (
                      <div>
                        <p className="font-medium text-sm mb-2 flex items-center gap-1"><Zap size={14} className="text-purple-500" /> Multiplier Bonuses</p>
                        {selectedCalc.multiplier_bonuses.map((m, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded mb-1 bg-purple-50">
                            <div>
                              <p className="text-sm font-medium">{m.multiplier_name}</p>
                              <p className="text-xs text-slate-500">{m.description}</p>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">+{m.bonus.toLocaleString('en-IN')}</Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Grand total */}
                    <div className="border-t pt-3 space-y-1 text-sm">
                      <div className="flex justify-between"><span>Base Total</span><span>{selectedCalc.total_base.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between text-amber-600"><span>Slab Bonus</span><span>+{selectedCalc.total_slab_bonus.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between text-emerald-600"><span>Target Bonus</span><span>+{selectedCalc.total_target_bonus.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between text-purple-600"><span>Multiplier Bonus</span><span>+{selectedCalc.total_multiplier_bonus.toLocaleString('en-IN')}</span></div>
                      {(selectedCalc.total_deductions || 0) > 0 && (
                        <div className="flex justify-between text-red-600"><span>Deductions</span><span>-{selectedCalc.total_deductions.toLocaleString('en-IN')}</span></div>
                      )}
                      <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                        <span>Grand Total</span>
                        <span className="text-emerald-700">{selectedCalc.grand_total.toLocaleString('en-IN')}</span>
                      </div>
                    </div>

                    {/* Deduction details */}
                    {selectedCalc.deduction_details?.length > 0 && (
                      <div className="mt-3">
                        <p className="font-semibold text-sm text-red-700 mb-2 flex items-center gap-1"><MinusCircle size={14} /> Deduction Breakdown</p>
                        {selectedCalc.deduction_details.map((d, i) => (
                          <div key={i} className="flex justify-between text-sm border-b border-red-100 py-1.5">
                            <div>
                              <span className="text-slate-700">{d.rule_name}</span>
                              <span className="text-xs text-slate-400 ml-2">({d.description})</span>
                            </div>
                            <span className="text-red-600 font-medium">-{d.deduction.toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* ===== REPORTS TAB ===== */}
          <TabsContent value="reports" className="space-y-6 mt-6">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={String(reportMonth)} onValueChange={v => { setReportMonth(parseInt(v)); }}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={String(reportYear)} onValueChange={v => { setReportYear(parseInt(v)); }}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchReport}>Refresh</Button>
              {report && report.total_employees > 0 && (
                <Button variant="outline" onClick={() => exportExcel(reportMonth, reportYear)} className="gap-1.5" data-testid="report-export-btn">
                  <Download size={14} /> Export Excel
                </Button>
              )}
            </div>

            {!report || report.total_employees === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <TrendingUp size={40} className="mx-auto mb-3 text-slate-300" />
                  <p>No report data for {MONTH_NAMES[reportMonth - 1]} {reportYear}</p>
                  <p className="text-xs mt-1">Run salary calculations first</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* KPI cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-indigo-600">Employees</p>
                      <p className="text-3xl font-bold text-indigo-700">{report.total_employees}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-emerald-600">Total Payout</p>
                      <p className="text-3xl font-bold text-emerald-700">{report.total_payout.toLocaleString('en-IN')}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-amber-600">Avg Payout</p>
                      <p className="text-3xl font-bold text-amber-700">{report.avg_payout.toLocaleString('en-IN')}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <CardContent className="p-4">
                      <p className="text-xs text-purple-600">Status</p>
                      <div className="flex gap-1 mt-1">
                        {Object.entries(report.status_breakdown || {}).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="text-xs">{k}: {v}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-base">Payout by Task Type</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={report.by_task_type}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="task_type" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="units" fill="#4F46E5" name="Units" />
                          <Bar dataKey="amount" fill="#10B981" name="Amount" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle className="text-base">Payout by Project</CardTitle></CardHeader>
                    <CardContent>
                      {(report.by_project || []).length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={report.by_project} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis dataKey="project" type="category" tick={{ fontSize: 11 }} width={120} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="amount" fill="#06B6D4" name="Amount" />
                            <Bar dataKey="units" fill="#8B5CF6" name="Units" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-8">No project-wise data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-base">Payout by Department</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={report.by_department} cx="50%" cy="50%" outerRadius={80} dataKey="payout" nameKey="department" label={({ name, value }) => `${name}: ${value.toLocaleString('en-IN')}`}>
                            {(report.by_department || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Top earners */}
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award size={16} className="text-amber-500" /> Top Earners</CardTitle></CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-slate-500">
                          <th className="pb-2 font-medium">#</th>
                          <th className="pb-2 font-medium">Employee</th>
                          <th className="pb-2 font-medium">Code</th>
                          <th className="pb-2 font-medium text-center">Units</th>
                          <th className="pb-2 font-medium text-right">Payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.top_earners.map((e, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2 font-bold text-indigo-600">{i + 1}</td>
                            <td className="py-2 font-medium">{e.employee_name}</td>
                            <td className="py-2 text-slate-500">{e.employee_code}</td>
                            <td className="py-2 text-center">{e.total_units}</td>
                            <td className="py-2 text-right font-bold text-emerald-600">{e.grand_total.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== DIALOG ===== */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editItem ? 'Edit' : 'Add'} {dialogType === 'task_type' ? 'Task Type' : dialogType === 'project' ? 'Project' : dialogType === 'rate_card' ? 'Rate Card' : dialogType === 'target' ? 'Target Bonus' : dialogType === 'multiplier' ? 'Multiplier' : dialogType === 'deduction_rule' ? 'Deduction Rule' : 'PPU Entry'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {dialogType === 'task_type' && (
                <>
                  <div><Label>Name</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Merchant Onboarding" data-testid="task-type-name" /></div>
                  <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" /></div>
                </>
              )}

              {dialogType === 'project' && (
                <>
                  <div><Label>Project Name</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. HDFC Bank - North Zone" data-testid="project-name" /></div>
                  <div><Label>Client / Company</Label><Input value={form.client || ''} onChange={e => setForm({ ...form, client: e.target.value })} placeholder="e.g. HDFC Bank" /></div>
                  <div><Label>Region / Zone</Label><Input value={form.region || ''} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="e.g. North Zone" /></div>
                  <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" /></div>
                </>
              )}

              {dialogType === 'rate_card' && (
                <>
                  <div>
                    <Label>Project (optional — leave empty for general rate)</Label>
                    <Select value={form.project_id || 'general'} onValueChange={v => setForm({ ...form, project_id: v === 'general' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General (All Projects)</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Task Type</Label>
                    <Select value={form.task_type_id} onValueChange={v => setForm({ ...form, task_type_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select task type" /></SelectTrigger>
                      <SelectContent>{taskTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Designation</Label>
                    <Select value={form.designation_id} onValueChange={v => setForm({ ...form, designation_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
                      <SelectContent>
                        {designations.slice(0, 50).map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Base Rate (per unit)</Label><Input type="number" value={form.base_rate || ''} onChange={e => setForm({ ...form, base_rate: e.target.value })} placeholder="e.g. 50" data-testid="base-rate-input" /></div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Slab Tiers (higher rate after threshold)</Label>
                      <Button size="sm" variant="outline" onClick={addSlab} type="button"><Plus size={12} className="mr-1" /> Add Slab</Button>
                    </div>
                    {(form.slabs || []).map((slab, i) => (
                      <div key={i} className="flex gap-2 items-center mb-2">
                        <Input type="number" className="w-24" placeholder="Min units" value={slab.min_units || ''} onChange={e => updateSlab(i, 'min_units', e.target.value)} />
                        <span className="text-slate-400">to</span>
                        <Input type="number" className="w-24" placeholder="Max (empty=no limit)" value={slab.max_units || ''} onChange={e => updateSlab(i, 'max_units', e.target.value)} />
                        <span className="text-slate-400">@</span>
                        <Input type="number" className="w-20" placeholder="Rate" value={slab.rate || ''} onChange={e => updateSlab(i, 'rate', e.target.value)} />
                        <span className="text-slate-400">/unit</span>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeSlab(i)}><Trash2 size={12} /></Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {dialogType === 'target' && (
                <>
                  <div><Label>Target Name</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Monthly Onboarding Star" /></div>
                  <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                  <div>
                    <Label>Target Type</Label>
                    <Select value={form.target_type} onValueChange={v => setForm({ ...form, target_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="total_units">Total Units (all tasks)</SelectItem>
                        <SelectItem value="individual_task">Individual Task Type</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.target_type === 'individual_task' && (
                    <div>
                      <Label>Task Type</Label>
                      <Select value={form.task_type_id} onValueChange={v => setForm({ ...form, task_type_id: v })}>
                        <SelectTrigger><SelectValue placeholder="Select task" /></SelectTrigger>
                        <SelectContent>{taskTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Project (optional - leave empty for all projects)</Label>
                    <Select value={form.project_id || 'all'} onValueChange={v => setForm({ ...form, project_id: v === 'all' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Projects</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Target Units</Label><Input type="number" value={form.target_units || ''} onChange={e => setForm({ ...form, target_units: e.target.value })} placeholder="e.g. 100" /></div>
                    <div><Label>Bonus Amount</Label><Input type="number" value={form.bonus_amount || ''} onChange={e => setForm({ ...form, bonus_amount: e.target.value })} placeholder="e.g. 5000" /></div>
                  </div>
                  <div>
                    <Label>Frequency</Label>
                    <Select value={form.frequency || 'monthly'} onValueChange={v => setForm({ ...form, frequency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily (bonus per qualifying day)</SelectItem>
                        <SelectItem value="weekly">Weekly (bonus per qualifying week)</SelectItem>
                        <SelectItem value="monthly">Monthly (one-time bonus if target met)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Designation (optional - leave empty for all)</Label>
                    <Select value={form.designation_id || 'all'} onValueChange={v => setForm({ ...form, designation_id: v === 'all' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Designations</SelectItem>
                        {designations.slice(0, 50).map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {dialogType === 'multiplier' && (
                <>
                  <div><Label>Name</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. All-Rounder Bonus" /></div>
                  <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                  <div>
                    <Label>Required Task Types (select all that must be completed)</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {taskTypes.map(t => (
                        <Badge
                          key={t.id}
                          className={`cursor-pointer ${(form.required_task_type_ids || []).includes(t.id) ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                          onClick={() => toggleTaskType(t.id)}
                        >
                          {t.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Multiplier</Label><Input type="number" step="0.1" value={form.multiplier || ''} onChange={e => setForm({ ...form, multiplier: e.target.value })} placeholder="e.g. 1.5" /></div>
                    <div>
                      <Label>Apply To</Label>
                      <Select value={form.apply_to} onValueChange={v => setForm({ ...form, apply_to: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily (per qualifying day)</SelectItem>
                          <SelectItem value="monthly">Monthly (if all types done in month)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {dialogType === 'entry' && (
                <>
                  <div>
                    <Label>Employee</Label>
                    <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>
                        {employees.slice(0, 100).map(e => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.employee_id} - {e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Date</Label><Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
                  <div>
                    <Label>Task Type</Label>
                    <Select value={form.task_type_id} onValueChange={v => setForm({ ...form, task_type_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select task" /></SelectTrigger>
                      <SelectContent>{taskTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Project</Label>
                    <Select value={form.project_id || 'general'} onValueChange={v => setForm({ ...form, project_id: v === 'general' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Units Completed</Label><Input type="number" value={form.units || ''} onChange={e => setForm({ ...form, units: e.target.value })} placeholder="e.g. 5" data-testid="units-input" /></div>
                    <div><Label>Failed Units</Label><Input type="number" value={form.failed_units || ''} onChange={e => setForm({ ...form, failed_units: e.target.value })} placeholder="0" data-testid="failed-units-input" /></div>
                  </div>
                  <div><Label>Notes (optional)</Label><Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                </>
              )}

              {dialogType === 'deduction_rule' && (
                <>
                  <div><Label>Rule Name</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Failed Task Penalty" data-testid="deduction-name" /></div>
                  <div><Label>Description</Label><Input value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                  <div>
                    <Label>Task Type (optional - leave empty for all)</Label>
                    <Select value={form.task_type_id || 'all'} onValueChange={v => setForm({ ...form, task_type_id: v === 'all' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Task Types</SelectItem>
                        {taskTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Deduction Type</Label>
                    <Select value={form.deduction_type} onValueChange={v => setForm({ ...form, deduction_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Flat Rate (per failed unit)</SelectItem>
                        <SelectItem value="slab">Slab-Based (by range)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.deduction_type === 'flat' && (
                    <div><Label>Deduction per Failed Unit (Rs.)</Label><Input type="number" value={form.flat_rate || ''} onChange={e => setForm({ ...form, flat_rate: e.target.value })} placeholder="e.g. 50" /></div>
                  )}
                  {form.deduction_type === 'slab' && (
                    <div>
                      <Label>Slab Tiers</Label>
                      <div className="space-y-2 mt-1">
                        {(form.slabs || []).map((slab, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input type="number" placeholder="Min" className="w-20" value={slab.min_failed || ''} onChange={e => { const s = [...form.slabs]; s[i] = { ...s[i], min_failed: e.target.value }; setForm({ ...form, slabs: s }); }} />
                            <span className="text-slate-400">-</span>
                            <Input type="number" placeholder="Max" className="w-20" value={slab.max_failed || ''} onChange={e => { const s = [...form.slabs]; s[i] = { ...s[i], max_failed: e.target.value }; setForm({ ...form, slabs: s }); }} />
                            <span className="text-slate-400">failed =</span>
                            <Input type="number" placeholder="Rs." className="w-24" value={slab.deduction_amount || ''} onChange={e => { const s = [...form.slabs]; s[i] = { ...s[i], deduction_amount: e.target.value }; setForm({ ...form, slabs: s }); }} />
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => { const s = [...form.slabs]; s.splice(i, 1); setForm({ ...form, slabs: s }); }}><Trash2 size={12} /></Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => setForm({ ...form, slabs: [...(form.slabs || []), { min_failed: '', max_failed: '', deduction_amount: '' }] })}>
                          <Plus size={12} className="mr-1" /> Add Slab
                        </Button>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Designation (optional)</Label>
                    <Select value={form.designation_id || 'all'} onValueChange={v => setForm({ ...form, designation_id: v === 'all' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Designations</SelectItem>
                        {designations.slice(0, 50).map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={loading} data-testid="save-dialog-btn">
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* ===== BULK UPLOAD DIALOG ===== */}
        <Dialog open={showUploadDialog} onOpenChange={(v) => { if (!v) { setShowUploadDialog(false); setUploadResult(null); setUploadFile(null); } }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload size={18} className="text-indigo-500" /> Bulk PPU Data Upload
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Upload Type */}
              <div>
                <Label>Upload Type</Label>
                <Select value={uploadType} onValueChange={v => { setUploadType(v); setUploadFile(null); setUploadResult(null); }}>
                  <SelectTrigger data-testid="upload-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily Logs (one row per day per task)</SelectItem>
                    <SelectItem value="monthly">Monthly Aggregated (total units per task)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {uploadType === 'daily'
                    ? 'Each row: Employee ID, Date, Task Type, Project, Units, Notes'
                    : 'Each row: Employee ID, Task Type, Project, Total Units for the month'}
                </p>
              </div>

              {/* Month/Year (for monthly upload) */}
              {uploadType === 'monthly' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Month</Label>
                    <Select value={String(uploadMonth)} onValueChange={v => setUploadMonth(parseInt(v))}>
                      <SelectTrigger data-testid="upload-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Select value={String(uploadYear)} onValueChange={v => setUploadYear(parseInt(v))}>
                      <SelectTrigger data-testid="upload-year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Download Template */}
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Step 1: Download Template</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={downloadTemplate} className="gap-1.5" data-testid="download-daily-template-btn">
                    <Download size={13} /> Daily Template
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadMonthlyTemplate} className="gap-1.5" data-testid="download-monthly-template-btn">
                    <Download size={13} /> Monthly Template
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1.5">Templates include reference sheets for valid Task Types and Projects</p>
              </div>

              {/* File Input */}
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-700 mb-2">Step 2: Select Excel File</p>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={e => { setUploadFile(e.target.files[0] || null); setUploadResult(null); }}
                  className="cursor-pointer"
                  data-testid="bulk-upload-file-input"
                />
                {uploadFile && <p className="text-xs text-indigo-600 mt-1">{uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</p>}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="upload-result">
                  <p className="text-sm font-medium text-emerald-800 mb-1">Upload Complete</p>
                  <div className="flex gap-3 text-sm">
                    <span className="text-emerald-700">Created: <strong>{uploadResult.created}</strong></span>
                    <span className="text-blue-700">Updated: <strong>{uploadResult.updated}</strong></span>
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="text-xs font-medium text-red-600">Errors ({uploadResult.errors.length}):</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={() => downloadUploadErrorsCsv(uploadResult.errors, 'ppu-salary-upload-errors.csv')}
                          data-testid="download-upload-errors-csv"
                        >
                          Download CSV
                        </Button>
                      </div>
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-500">{formatUploadError(err)}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); setUploadResult(null); setUploadFile(null); }}>
                {uploadResult ? 'Close' : 'Cancel'}
              </Button>
              {!uploadResult && (
                <>
                  <Button variant="outline" onClick={handleBulkUpload} disabled={!uploadFile || uploading} data-testid="upload-only-btn">
                    {uploading ? 'Uploading...' : 'Upload Only'}
                  </Button>
                  <Button onClick={handleUploadAndCalculate} disabled={!uploadFile || uploading} className="gap-1.5" data-testid="upload-and-calc-btn">
                    <Calculator size={14} /> {uploading ? 'Processing...' : 'Upload & Calculate'}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PPUSalary;
